import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography
} from '@mui/material';
import { Close as CloseIcon, Person as PersonIcon } from '@mui/icons-material';
import { addInquiry } from 'src/ApiServices';
import { toast } from 'react-toastify';
import palette from 'src/theme/palette';
import useMediaQuery from '@mui/material/useMediaQuery';

const EditInquiryModal = ({ open, onClose, inquiry, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact_no: '',
    date: '',
    time: '',
    comment: ''
  });
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    if (inquiry) {
      setFormData({
        name: inquiry.name || '',
        email: inquiry.email || '',
        contact_no: inquiry.contact_no || '',
        date: inquiry.date || '',
        time: inquiry.time || '',
        comment: inquiry.comment || '',
        type: inquiry?.type?.id ? inquiry.type.id : 3
      });
    }
  }, [inquiry]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Payload
      const inquiryData = {
        id: inquiry.id,
        name: formData.name,
        email: formData.email,
        contact_no: formData.contact_no,
        date: formData.date,
        time: formData.time,
        comment: formData.comment,
        type: inquiry?.type?.id ? inquiry.type.id : 3
      };
      await addInquiry(inquiryData);

      toast.success('Inquiry updated successfully!', {
        position: 'top-right',
        autoClose: 3000
      });

      onSuccess();
    } catch (error) {
      console.error('Failed to update inquiry:', error);
      toast.error('Failed to update inquiry. Please try again.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          margin: { xs: 2, sm: 3 },
          maxHeight: isMobile ? '100vh' : '90vh',
          width: isMobile ? '100vw' : '1400px',
          margin: isMobile ? 0 : undefined
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: palette.primary.main,
          color: 'white',
          py: 2
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon />
            <Typography variant="h6" fontFamily="poppinsSemiBold">
              Edit Inquiry
            </Typography>
          </Box>
          <Button
            onClick={onClose}
            size="small"
            sx={{
              minWidth: 'auto',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Number"
                name="contact_no"
                value={formData.contact_no}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                label="Time"
                name="time"
                type="time"
                value={formData.time}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Comments/Requirements"
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                multiline
                rows={4}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ fontFamily: 'poppinsMedium' }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              fontFamily: 'poppinsMedium',
              backgroundColor: palette.primary.main,
              '&:hover': {
                backgroundColor: palette.primary.dark
              }
            }}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Inquiry'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditInquiryModal;
